import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '@/layouts/admin-layout'
import { AppHeader } from '@/components/app-header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Link, router } from '@inertiajs/react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface AdminAgentSkillsNewProps {
  valid_contexts: string[]
  skill_types: string[]
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
  }
}

function New({ valid_contexts, skill_types }: AdminAgentSkillsNewProps) {
  const { t } = useTranslation('admin')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [description, setDescription] = useState('')
  const [content, setContent] = useState('')
  const [skillType, setSkillType] = useState('')
  const [contexts, setContexts] = useState<string[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})

  const toggleContext = (context: string) => {
    if (contexts.includes(context)) {
      setContexts(contexts.filter(c => c !== context))
    } else {
      setContexts([...contexts, context])
    }
  }

  const generateSlug = (value: string) => {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 100)
  }

  const handleNameChange = (value: string) => {
    setName(value)
    // Auto-generate slug from name if slug is empty or matches the previous auto-generated value
    if (!slug || slug === generateSlug(name)) {
      setSlug(generateSlug(value))
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!name.trim()) {
      newErrors.name = t('agent_skills.new.errors.nameRequired')
    } else if (name.length > 255) {
      newErrors.name = t('agent_skills.new.errors.nameTooLong')
    }

    if (!slug.trim()) {
      newErrors.slug = t('agent_skills.new.errors.slugRequired')
    } else if (!/^[a-z0-9-]+$/.test(slug)) {
      newErrors.slug = t('agent_skills.new.errors.slugInvalid')
    } else if (slug.length > 100) {
      newErrors.slug = t('agent_skills.new.errors.slugTooLong')
    }

    if (!description.trim()) {
      newErrors.description = t('agent_skills.new.errors.descriptionRequired')
    } else if (description.length < 20) {
      newErrors.description = t('agent_skills.new.errors.descriptionTooShort')
    }

    if (!content.trim()) {
      newErrors.content = t('agent_skills.new.errors.contentRequired')
    }

    if (!skillType) {
      newErrors.skillType = t('agent_skills.new.errors.skillTypeRequired')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    router.post('/admin/agent_skills', {
      agent_skill: {
        name: name.trim(),
        slug: slug.toLowerCase().trim(),
        description: description.trim(),
        content: content,
        skill_type: skillType,
        applicable_contexts: contexts
      }
    }, {
      onFinish: () => setIsSubmitting(false)
    })
  }

  return (
    <>
      <AppHeader breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: t('agent_skills.index.title'), href: '/admin/agent_skills' },
        { label: t('agent_skills.new.title') }
      ]} />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">
              {/* Page Header */}
              <div className="mb-6 flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-foreground">{t('agent_skills.new.title')}</h1>
                  <p className="mt-2 text-sm text-muted-foreground">{t('agent_skills.new.description')}</p>
                </div>
                <Button variant="outline" asChild>
                  <Link href="/admin/agent_skills">{t('agent_skills.show.backToList')}</Link>
                </Button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Main Form */}
                  <div className="lg:col-span-2 space-y-6">
                    <Card className="shadow-none">
                      <CardHeader>
                        <CardTitle>{t('agent_skills.new.form.basicInfo')}</CardTitle>
                        <CardDescription>{t('agent_skills.new.form.basicInfoDescription')}</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">{t('agent_skills.new.form.name')} *</Label>
                          <Input
                            id="name"
                            value={name}
                            onChange={(e) => handleNameChange(e.target.value)}
                            placeholder={t('agent_skills.new.form.namePlaceholder')}
                            maxLength={255}
                          />
                          {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="slug">{t('agent_skills.new.form.slug')} *</Label>
                          <Input
                            id="slug"
                            value={slug}
                            onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                            placeholder={t('agent_skills.new.form.slugPlaceholder')}
                            maxLength={100}
                            className="font-mono"
                          />
                          <p className="text-xs text-muted-foreground">{t('agent_skills.new.form.slugHint')}</p>
                          {errors.slug && <p className="text-sm text-destructive">{errors.slug}</p>}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="description">{t('agent_skills.new.form.description')} *</Label>
                          <Textarea
                            id="description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={t('agent_skills.new.form.descriptionPlaceholder')}
                            rows={3}
                          />
                          <p className="text-xs text-muted-foreground">
                            {t('agent_skills.new.form.descriptionHint', { count: description.length })}
                          </p>
                          {errors.description && <p className="text-sm text-destructive">{errors.description}</p>}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="skillType">{t('agent_skills.new.form.skillType')} *</Label>
                          <Select value={skillType} onValueChange={setSkillType}>
                            <SelectTrigger>
                              <SelectValue placeholder={t('agent_skills.new.form.skillTypePlaceholder')} />
                            </SelectTrigger>
                            <SelectContent>
                              {skill_types.map((type) => (
                                <SelectItem key={type} value={type}>
                                  {t(`agent_skills.types.${type}`)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {errors.skillType && <p className="text-sm text-destructive">{errors.skillType}</p>}
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="shadow-none">
                      <CardHeader>
                        <CardTitle>{t('agent_skills.new.form.content')}</CardTitle>
                        <CardDescription>{t('agent_skills.new.form.contentDescription')}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Textarea
                          id="content"
                          value={content}
                          onChange={(e) => setContent(e.target.value)}
                          placeholder={t('agent_skills.new.form.contentPlaceholder')}
                          rows={15}
                          className="font-mono text-sm"
                        />
                        {errors.content && <p className="text-sm text-destructive mt-2">{errors.content}</p>}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Sidebar */}
                  <div className="space-y-6">
                    <Card className="shadow-none">
                      <CardHeader>
                        <CardTitle>{t('agent_skills.new.form.contexts')}</CardTitle>
                        <CardDescription>{t('agent_skills.new.form.contextsDescription')}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2">
                          {valid_contexts.map((context) => (
                            <Badge
                              key={context}
                              variant={contexts.includes(context) ? 'default' : 'outline'}
                              className="cursor-pointer"
                              onClick={() => toggleContext(context)}
                            >
                              {context}
                            </Badge>
                          ))}
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="shadow-none">
                      <CardHeader>
                        <CardTitle>{t('agent_skills.new.form.actions')}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <Button
                          type="submit"
                          className="w-full"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? t('agent_skills.new.form.creating') : t('agent_skills.new.form.create')}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full"
                          asChild
                        >
                          <Link href="/admin/agent_skills">{t('agent_skills.new.form.cancel')}</Link>
                        </Button>
                      </CardContent>
                    </Card>

                    <Card className="shadow-none bg-muted/50">
                      <CardHeader>
                        <CardTitle className="text-sm">{t('agent_skills.new.form.note')}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          {t('agent_skills.new.form.noteDescription')}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

New.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default New
